import { useFormikContext } from 'formik';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Container, Divider, Form, Transition } from 'semantic-ui-react';
import { ProFormUpdateFields } from '../pro-form-update-fields-type';
import ProPhoneNumbersField from '../pro-phone-numbers-field/pro-phone-numbers-field';
import SelectProViewField from 'components/select-pro-view-field/select-pro-view-field';
import FormField from 'components/form-field/form-field';
import FieldSuggestions, {
  Suggestion,
} from 'components/field-suggestions/field-suggestions';
import AccountsField from 'components/accounts-field/accounts-field';
import ProPriceField from 'components/pro-price-field/pro-price-field';
import HelpText from 'components/help-text/help-text';
import TradeFields from 'components/trade-fields/trade-fields';
import ConfirmModal from 'components/confirm-modal/confirm-modal';
import { PhoneNumber } from 'utils';
import ProContactsField from 'components/pro-contacts-field/pro-contacts-field';

type ProViewRouteProps = {
  bilikZoneId: number;
  proViewId: number;
  proPresentationId: number;
};

const ProViewRoute: FunctionComponent<ProViewRouteProps> = ({
  bilikZoneId,
  proViewId,
  proPresentationId,
}) => {
  const { values, errors, touched, setFieldValue, initialValues } =
    useFormikContext<ProFormUpdateFields>();

  const [callTrackingSuggestions, setCallTrackingSuggestions] = useState<
    Suggestion[]
  >([]);

  const [accountEmailSuggestions, setAccountEmailSuggestion] =
    useState<string[]>();

  const [contactConfigAlert, setContactConfigAlert] = useState<{
    isModalOpen: boolean;
    lastConfigChanged: 'call' | 'solicitation' | null;
  }>({
    isModalOpen: false,
    lastConfigChanged: null,
  });

  useEffect(() => {
    const isContactDisabled =
      values.isCallsDisabled && values.isSolicitationsDisabled;
    const isUpdate =
      values.isCallsDisabled !== initialValues.isCallsDisabled ||
      values.isSolicitationsDisabled !== initialValues.isSolicitationsDisabled;

    setContactConfigAlert((prevState) => ({
      ...prevState,
      isModalOpen: isContactDisabled && isUpdate,
    }));
  }, [values.isCallsDisabled, values.isSolicitationsDisabled]);

  const callTrackingForwardSuggestions = useMemo(
    () =>
      callTrackingSuggestions.map((suggestions) => ({
        ...suggestions,
        fields: {
          'callTrackingForward.telephone':
            suggestions.fields['callTrackingTarget.telephone'],
          'callTrackingForward.name':
            suggestions.fields['callTrackingTarget.name'],
        },
      })),
    [callTrackingSuggestions],
  );

  return (
    <>
      <Divider horizontal>Catégorie(s)</Divider>
      <HelpText>
        Les chiffres correspondent à : Nombre de pros de la zone assignés à la
        catégorie avec entre parenthèses ceux en catégorie principale et bis
      </HelpText>
      <TradeFields bilikZoneId={bilikZoneId} action="update-pro" />
      <Divider horizontal style={{ marginTop: '0px' }}>
        Contact(s)
      </Divider>
      <ProContactsField
        name="proPeople"
        currentProViewId={proViewId}
        onProContactsLoaded={(proPeople): void => {
          // Get proPeople that have a phone number
          const proPeopleWithTelephone = proPeople.filter(
            (proPerson) =>
              proPerson.telephone !== undefined &&
              proPerson.telephone !== null &&
              proPerson.telephone !== '',
          );

          // Build suggestions for FieldSuggestions
          setCallTrackingSuggestions(
            proPeopleWithTelephone.map((proPerson) => ({
              key: proPerson.id,
              label: (
                <>
                  {proPerson.givenName} {proPerson.familyName} -{' '}
                  {PhoneNumber.format(proPerson.telephone || 'error')}
                </>
              ),
              fields: {
                'callTrackingTarget.telephone': proPerson.telephone,
                'callTrackingTarget.name': `${proPerson.givenName} ${proPerson.familyName}`,
              },
            })),
          );

          setAccountEmailSuggestion(
            proPeople
              .map((proPerson) => proPerson.email || 'null')
              .filter((proPersonEmail) => proPersonEmail !== 'null'),
          );
        }}
      />

      <Divider horizontal style={{ marginTop: '50px' }}>
        Accès espace pro
      </Divider>
      <AccountsField
        name="accounts"
        currentProViewId={proViewId}
        accountEmailSuggestions={accountEmailSuggestions}
      />
      <FormField
        type="email"
        name="emailTarget"
        label="Email cible"
        placeholder="Ex: example@email.fr"
        helpText="Adresse email recevant tous les mails issus de notre plateforme (demande mail, notification d'un nouvel avis)"
        required
      />
      <Divider horizontal style={{ marginTop: '50px' }}>
        Call tracking
      </Divider>
      <ProPhoneNumbersField proViewId={proViewId} />
      <FieldSuggestions
        label="Numéro de téléphone cible"
        suggestions={callTrackingSuggestions}
        placeholder="Sélectionnez un numéro..."
        required
      >
        <FormField
          type="telephone"
          label="Numéro"
          name="callTrackingTarget.telephone"
          placeholder="Ex: 06 44 64 27 74"
          required
        />
        <FormField
          type="text"
          placeholder="Ex: Nom"
          label="Libellé"
          name="callTrackingTarget.name"
          required
        />
      </FieldSuggestions>
      <Divider hidden />
      <FormField
        type="checkbox"
        name="callTrackingForward.isActive"
        label="Redirection d'appel"
        helpText="Permet de rediriger l'appel vers un autre numéro si pas de réponse"
      />
      <Transition.Group duration={250}>
        {values.callTrackingForward && values.callTrackingForward.isActive ? (
          <>
            <FieldSuggestions
              label="Numéro de téléphone redirigé"
              suggestions={callTrackingForwardSuggestions}
              placeholder="Sélectionnez un numéro..."
              required
            >
              <FormField
                label="Numéro"
                type="telephone"
                name="callTrackingForward.telephone"
                placeholder="Ex: 06 44 64 27 74"
                required
              />
              <FormField
                type="text"
                placeholder="Ex: Nom"
                label="Libellé"
                name="callTrackingForward.name"
                required
              />
            </FieldSuggestions>
            <FormField
              type="number"
              placeholder="Ex: 12"
              helpText="Assurez-vous que le répondeur de la première cible ne se déclenche pas, sinon la redirection échouera."
              label="Temps avant redirection de l'appel (secondes)"
              name="callTrackingForward.timeout"
              required
            />
          </>
        ) : null}
      </Transition.Group>
      <Divider horizontal style={{ marginTop: '50px' }}>
        Collègues
      </Divider>
      <SelectProViewField
        name="colleagues"
        label="Collègues"
        helpText="Collègues de Bilik avec qui le pro travaille (Même zone - Listé sur la fiche du pro)"
        filters={{
          exceptProPresentationId: proPresentationId,
          exceptStatus: ['archived'],
        }}
        multiple
        selection
        search
        clearable
      />
      <Divider hidden />
      <Divider hidden />
      <Divider horizontal>Tarif(s)</Divider>
      <ProPriceField />
      <FormField
        type="textarea"
        label="Commentaire"
        name="priceComment"
        placeholder="Ex: Pas de dépannage les nuits, samedis et dimanches."
        rows={3}
      />
      <Divider horizontal style={{ marginTop: '50px' }}>
        Paramètres
      </Divider>
      <FormField
        type="checkbox"
        name="isEnglishSpeaker"
        label="Parle couramment anglais"
        helpText='À cocher uniquement si le pro parle couramment anglais. Le badge "🇬🇧" sera ajouté sur sa fiche.'
      />
      <FormField
        type="checkbox"
        name="isSolicitationsDisabled"
        label="Désactiver les demandes mails"
        helpText='Désactive les demandes mails vers le pro: le bouton "Envoyer un mail" devient indisponible sur la fiche du pro'
        onChange={(e): void => {
          setContactConfigAlert({
            ...contactConfigAlert,
            lastConfigChanged: 'solicitation',
          });
        }}
      />
      <FormField
        type="checkbox"
        name="isSmsDisabled"
        label="Désactiver les SMS"
        helpText="Désactive les SMS vers le pro : le particulier reçoit un sms qui le redirige vers le formulaire de demande mail"
      />
      <FormField
        type="checkbox"
        name="isCallsDisabled"
        label="Désactiver les appels téléphoniques"
        helpText="Désactive les appels vers le pro: le numéro de téléphone est caché sur la fiche du pro"
        onChange={(e): void => {
          setContactConfigAlert({
            ...contactConfigAlert,
            lastConfigChanged: 'call',
          });
        }}
      />
      <FormField
        type="checkbox"
        name="isCallAlertActivated"
        label="Activer les alertes d'appels par sms"
        helpText="Active les alertes d'appels par sms vers le pro: le pro reçoit un sms à chaque appel provenant de Bilik"
      />
      <FormField
        type="checkbox"
        name="isCallRecordable"
        label="Enregistrer les appels"
        helpText="Active l'enregistrement des appels, le pro peut écouter les appels reçus depuis son espace pro"
      />
      <Divider horizontal>Paramétrage du suivi</Divider>
      <FormField type="checkbox" name="hasContact" label="Relance contact" />
      <Transition.Group duration={250}>
        {values.hasContact ? (
          <Container fluid>
            <Form.Group widths="equal">
              <FormField
                noFastField
                disabled={!values.hasContact}
                type="number"
                placeholder="Ex: 2"
                label="Temps moyen de réponse (jours ouvrés)"
                helpText={
                  <>
                    Temps entre la demande et la programmation du mail de suivi
                    (2/4) - Avez-vous reçu une réponse ?
                  </>
                }
                name="averageResponseDays"
                required
                min={1}
              />
            </Form.Group>
          </Container>
        ) : null}
      </Transition.Group>
      <FormField type="checkbox" name="hasQuotation" label="Relance devis" />
      <Transition.Group duration={250}>
        {values.hasQuotation ? (
          <Container fluid>
            <Form.Group widths="equal">
              <FormField
                noFastField
                disabled={!values.hasQuotation}
                type="number"
                placeholder="Ex: 21"
                label="Temps moyen avant devis (jours)"
                helpText={
                  <>
                    Temps entre la demande et la programmation du mail de suivi
                    (3/4) - Est-ce que tout se passe bien ?
                  </>
                }
                name="averageQuotationDays"
                required
                min={2}
              />
            </Form.Group>
          </Container>
        ) : null}
      </Transition.Group>
      <FormField
        noFastField
        type="number"
        placeholder="Ex: 66"
        label="Temps moyen avant réalisation (jours)"
        helpText={
          <>
            Temps entre la demande et la programmation du mail de suivi (4/4) -
            La prestation est-elle terminée ?
          </>
        }
        name="averageProjectDays"
        required
        min={3}
      />
      <ConfirmModal
        header="Confirmation"
        content={
          <>
            <p>Êtes-vous sûr de vouloir désactiver :</p>
            <ul>
              <li>Les appels téléphoniques</li>
              <li>Les demandes mails</li>
            </ul>
            <p>Le pro risque de ne plus être contacté.</p>
          </>
        }
        modalOpen={contactConfigAlert.isModalOpen}
        onDecline={(): void => {
          if (contactConfigAlert.lastConfigChanged === 'call') {
            setFieldValue('isCallsDisabled', false);
          }
          if (contactConfigAlert.lastConfigChanged === 'solicitation') {
            setFieldValue('isSolicitationsDisabled', false);
          }
        }}
      />
    </>
  );
};

export default ProViewRoute;
